import React, { useState } from 'react';
import { Folder, FileText, FileCode, FileSpreadsheet, FileImage, FileAudio, File, Presentation, AlertCircle, Loader2, CheckSquare, Square, Lock } from 'lucide-react';
import { formatLocalDate } from '../../utils/datetime';
import { useLanguage } from '../../i18n';

// ── File-type colour system (Color matching folders #180) ────────────────────
// ONE dimmed, matte palette shared by BOTH the grid and the list (linear) views,
// so a given file type renders the EXACT same colour in either mode. The colours
// are intentionally softer/less-saturated than the old vivid grid palette
// (red-500 / emerald-600 / blue-600 / orange-500 / yellow-500) — a lighter -400
// weight reads as a matte pastel instead of a shiny neon icon.
const FILE_TYPE_COLOR = {
    folder: 'text-amber-400 dark:text-amber-300',   // Folders → yellow (muted gold)
    pdf:    'text-red-400 dark:text-red-300',        // PDF → red
    excel:  'text-green-400 dark:text-green-300',    // Excel → green
    word:   'text-blue-400 dark:text-blue-300',      // Word → blue
    ppt:    'text-orange-400 dark:text-orange-300',  // PowerPoint → orange
    image:  'text-purple-400 dark:text-purple-300',
    audio:  'text-violet-400 dark:text-violet-300',
    code:   'text-teal-400 dark:text-teal-300',
    text:   'text-slate-400 dark:text-slate-300',
    file:   'text-gray-400 dark:text-gray-500',
};

// Coarse type key used for BOTH icon-shape and colour selection. Kept in lock-step
// with getTypeLabel() below (same ext/mime buckets, same precedence).
const getFileTypeKey = (filename, mimeType, isFolder) => {
    if (isFolder) return 'folder';
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'tiff'].includes(ext) || mimeType?.startsWith('image/')) return 'image';
    if (ext === 'pdf' || mimeType === 'application/pdf') return 'pdf';
    if (['xlsx', 'xls', 'csv'].includes(ext) || mimeType?.includes('spreadsheet')) return 'excel';
    if (['doc', 'docx'].includes(ext) || mimeType?.includes('word')) return 'word';
    if (['ppt', 'pptx'].includes(ext) || mimeType?.includes('presentation')) return 'ppt';
    if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'webm', 'aac'].includes(ext) || mimeType?.startsWith('audio/')) return 'audio';
    if (['txt', 'md', 'markdown', 'rtf'].includes(ext)) return 'text';
    if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'h', 'go', 'rs', 'php', 'rb', 'swift', 'json', 'html', 'css'].includes(ext)) return 'code';
    return 'file';
};

// The single source of truth for a file/folder icon colour — used by BOTH views.
const getFileTypeColorClass = (filename, mimeType, isFolder) =>
    FILE_TYPE_COLOR[getFileTypeKey(filename, mimeType, isFolder)] || FILE_TYPE_COLOR.file;

// Per-type icon SHAPE for the grid view (the list view keeps a single generic
// File/Folder shape + a text "Type" column — Status evolution #80).
const ICON_BY_KEY = {
    folder: Folder,
    pdf: FileText,
    excel: FileSpreadsheet,
    word: FileText,
    ppt: Presentation,
    image: FileImage,
    audio: FileAudio,
    code: FileCode,
    text: FileText,
    file: File,
};

// Grid icon: per-type shape + the shared dimmed colour.
const getFileIcon = (filename, mimeType, size = 32) => {
    const key = getFileTypeKey(filename, mimeType, false);
    const Icon = ICON_BY_KEY[key] || File;
    return <Icon size={size} data-testid="fm-item-icon" data-filetype={key} className={getFileTypeColorClass(filename, mimeType, false)} />;
};

// Professional, text-only file-type label for the list "Type" column (Status
// evolution #80) — replaces the colourful per-type "emoji-style" icons. `t` is
// passed in by the calling component (this is a module-level helper, so it
// cannot call the useLanguage() hook itself); a raw file extension (the final
// fallback) is a technical token, not translated copy, and is left as-is.
const getTypeLabel = (filename, mimeType, isFolder, t) => {
    if (isFolder) return t('fmTypeFolder', 'Folder');
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'tiff'].includes(ext) || mimeType?.startsWith('image/')) return t('fmTypeImage', 'Image');
    if (ext === 'pdf' || mimeType === 'application/pdf') return t('fmTypePdf', 'PDF');
    if (['xlsx', 'xls', 'csv'].includes(ext) || mimeType?.includes('spreadsheet')) return t('fmTypeExcel', 'Excel');
    if (['doc', 'docx'].includes(ext) || mimeType?.includes('word')) return t('fmTypeWord', 'Word');
    if (['ppt', 'pptx'].includes(ext) || mimeType?.includes('presentation')) return t('fmTypePpt', 'PPT');
    if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'webm', 'aac'].includes(ext) || mimeType?.startsWith('audio/')) return t('fmTypeAudio', 'Audio');
    if (['txt', 'md', 'markdown', 'rtf'].includes(ext)) return t('fmTypeText', 'Text');
    if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'h', 'go', 'rs', 'php', 'rb', 'swift', 'json', 'html', 'css'].includes(ext)) return t('fmTypeCode', 'Code');
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return t('fmTypeArchive', 'Archive');
    return ext ? ext.toUpperCase() : t('fmTypeFile', 'File');
};

// ── The file-extension chip (design §11 "File Manager") ──────────────────────
// The design replaces the list view's per-type icon set with a 30×22 monospace
// chip derived from the extension. We keep the per-type COLOUR on it rather than
// the design's flat `rgba(255,255,255,0.65)`: #180 made "one colour per type,
// identical in both views" a product contract (and color-matching-folders.spec.js
// enforces it), so the chip inherits the tint instead of discarding it. Folders
// have no extension and keep their folder icon — exactly as the source screenshot
// (11-file-manager.png) renders them.
const getExtensionLabel = (filename) => {
    const parts = (filename || '').split('.');
    if (parts.length < 2) return '—';
    const ext = parts.pop();
    return ext ? ext.slice(0, 4) : '—';
};

// Format file size
const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// Format an upload date in the viewer's LOCAL timezone. formatLocalDate treats a
// naive (no-tz) server timestamp as UTC before localizing, so a file uploaded late
// in the day isn't shown on the wrong calendar day for users far from UTC
// (Localization of Upload Timestamps #63).
const formatDate = (dateStr) => formatLocalDate(dateStr);

// Get status indicator for document processing. `t` is passed in by the
// calling component (module-level helper, no hook access of its own) — the
// PO's upload-status wording ("Uploading" / "Processing…") lives here.
//
// ⚠️ The `color` strings below are NOT decoration — both item components
// compare them by VALUE (`statusIndicator?.color === 'text-red-500'`) to tint
// the whole row/tile on a failed upload. Renaming them to `text-pai-red` etc.
// silently breaks that branch, so they were left on the Tailwind palette; the
// four tones read correctly on this ground already.
const getStatusIndicator = (status, t) => {
    switch (status) {
        case 'failed':
            return {
                icon: <AlertCircle size={14} className="text-red-500" />,
                color: 'text-red-500',
                label: t('fmStatusFailed', 'Failed to process')
            };
        case 'processing':
            return {
                icon: <Loader2 size={14} className="text-yellow-500 animate-spin" />,
                color: 'text-yellow-500',
                label: t('fmStatusProcessing', 'Processing...')
            };
        case 'pending':
            return {
                icon: <Loader2 size={14} className="text-gray-400" />,
                color: 'text-gray-400',
                label: t('fmStatusPending', 'Pending')
            };
        case 'completed':
        default:
            return null; // No indicator for completed
    }
};

export const FileManagerItemGrid = ({
    item,
    type,
    isSelected,
    onSelect,
    onOpen,
    onContextMenu,
    onDragStart,
    onDragEnd,
    onDrop,
    isDragging,
    selectedFolders,
    selectedDocuments,
    selectionMode
}) => {
    const { t } = useLanguage();
    const [isDragOver, setIsDragOver] = useState(false);
    const isFolder = type === 'folder';
    const name = isFolder ? item.name : item.original_filename;

    const handleDragStart = (e) => {
        // Set drag data
        const dragData = {
            type,
            itemId: item.id,
            // Include all selected items if this item is selected
            selectedFolders: isSelected ? Array.from(selectedFolders || []) : [],
            selectedDocuments: isSelected ? Array.from(selectedDocuments || []) : []
        };

        // If dragging an unselected item, just drag that item
        if (!isSelected) {
            if (type === 'folder') {
                dragData.selectedFolders = [item.id];
            } else {
                dragData.selectedDocuments = [item.id];
            }
        }

        e.dataTransfer.setData('application/json', JSON.stringify(dragData));

        // Rich payload for the chat-side reference-chip flow. Consumers
        // outside the FileManager (DocumentsModal backdrop, MainOfficeView)
        // pull this type instead of the move-internal `application/json`.
        const refPayload = {
            type,
            id: item.id,
            name,
            mime_type: item.mime_type || null,
            file_size: item.file_size ?? null,
            // Carry batch info so a single drag of a multi-selection adds them all.
            selectedFolders: dragData.selectedFolders,
            selectedDocuments: dragData.selectedDocuments,
        };
        e.dataTransfer.setData('application/x-document-ref', JSON.stringify(refPayload));
        e.dataTransfer.effectAllowed = 'copyMove';

        try {
            window.dispatchEvent(new CustomEvent('omnis:document-drag-start', { detail: refPayload }));
        } catch (_) { /* ignore */ }

        if (onDragStart) onDragStart(e, type, item);
    };

    const handleDragOver = (e) => {
        // Only folders can be drop targets
        if (!isFolder) return;

        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (!isFolder) return;

        try {
            const dragData = JSON.parse(e.dataTransfer.getData('application/json'));

            // Don't drop on self
            if (dragData.type === 'folder' && dragData.selectedFolders?.includes(item.id)) {
                return;
            }

            if (onDrop) {
                onDrop(item.id, dragData.selectedFolders || [], dragData.selectedDocuments || []);
            }
        } catch (err) {
            console.error('Drop error:', err);
        }
    };

    const handleDragEnd = (e) => {
        try {
            window.dispatchEvent(new CustomEvent('omnis:document-drag-end'));
        } catch (_) { /* ignore */ }
        if (onDragEnd) onDragEnd(e);
    };

    // Get status indicator for non-folder items
    const statusIndicator = !isFolder ? getStatusIndicator(item.processing_status, t) : null;

    // Build tooltip text
    const itemCountText = t('fmItemCount', '{n} item{s}', { n: item.item_count || 0, s: item.item_count !== 1 ? 's' : '' });
    const tooltipText = isFolder
        ? `${name}\n${itemCountText}`
        : `${name}\n${formatSize(item.file_size)}\n${formatDate(item.created_at)}${statusIndicator ? `\n${t('fmStatusLine', 'Status: {label}', { label: statusIndicator.label })}` : ''}`;

    const isProtected = isFolder && !!item.is_protected;

    return (
        <div
            data-item-id={item.id}
            data-item-type={type}
            data-protected={isProtected ? 'true' : undefined}
            draggable
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={(e) => {
                e.stopPropagation();
                // Single click OPENS (folder navigate / document preview) — modern
                // web behaviour (One Click #69). In selection mode, or with a
                // modifier, it selects/toggles instead so bulk-select still works.
                if (selectionMode || e.ctrlKey || e.metaKey || e.shiftKey) onSelect(item.id, type, e);
                else onOpen(item.id, type);
            }}
            onContextMenu={(e) => onContextMenu(e, type, item)}
            title={tooltipText}
            className={`
                flex flex-col items-center p-4 rounded-row cursor-pointer transition-colors relative border border-transparent
                ${isSelected
                    ? 'border-white/80 bg-white/[0.055] shadow-glow-sm'
                    : 'hover:bg-white/[0.07]'
                }
                ${isDragOver && isFolder
                    ? 'border-pai-green/60 bg-pai-green/[0.10]'
                    : ''
                }
                ${isDragging ? 'opacity-50' : ''}
                ${statusIndicator?.color === 'text-red-500' ? 'border-pai-red/40' : ''}
            `}
        >
            {/* Selection checkbox overlay — visible on every item while in selection
                mode so the user can tick a custom set (Select Mission #64). The item's
                own click handler toggles it, so the box just reflects isSelected.
                Financial Folders (#145): a protected system folder shows a Lock here
                instead — it can't be selected (so it can't be batch-deleted). */}
            {selectionMode && (
                isProtected ? (
                    <div className="absolute top-2 left-2 z-10" data-testid="fm-protected-lock" title={t('fmProtectedNoSelect', "System folder — can't be selected or deleted")}>
                        <Lock size={18} className="text-white/40" />
                    </div>
                ) : (
                    <div className="absolute top-2 left-2 z-10" data-testid="fm-item-checkbox" aria-checked={isSelected} role="checkbox">
                        {isSelected
                            ? <CheckSquare size={18} className="text-white" />
                            : <Square size={18} className="text-white/40" />}
                    </div>
                )
            )}

            {/* Status badge for failed/processing documents */}
            {statusIndicator && (
                <div className="absolute top-2 right-2" title={statusIndicator.label}>
                    {statusIndicator.icon}
                </div>
            )}

            {/* Protected system-folder lock (Financial Folders #145) — these
                /Financials folders can be renamed but never deleted/moved. In
                selection mode the lock shows in the checkbox slot instead. */}
            {isProtected && !selectionMode && (
                <div className="absolute top-2 right-2" data-testid="fm-protected-lock" title={t('fmProtectedNoDelete', "System folder — can't be deleted")}>
                    <Lock size={14} className="text-white/40" />
                </div>
            )}

            {/* Icon — per-type shape + the shared dimmed colour (Color matching
                folders #180). The folder is filled with its own (amber) colour via
                fill-current so it matches the list-view folder tint exactly. */}
            <div className="mb-2">
                {isFolder ? (
                    <Folder size={48} data-testid="fm-item-icon" data-filetype="folder" className={`${getFileTypeColorClass(name, item.mime_type, true)} fill-current ${isDragOver ? 'scale-110 transition-transform' : ''}`} />
                ) : (
                    getFileIcon(name, item.mime_type, 32)
                )}
            </div>

            {/* Name */}
            <div className="text-center w-full">
                <p className={`text-[13.5px] font-semibold truncate max-w-[120px] mx-auto ${statusIndicator?.color === 'text-red-500' ? 'text-pai-red' : 'text-white'}`}>
                    {name}
                </p>
                {!isFolder && (
                    <p className={`text-xs mt-1 tnum ${statusIndicator ? statusIndicator.color : 'text-pai-meta'}`}>
                        {statusIndicator ? statusIndicator.label : formatSize(item.file_size)}
                    </p>
                )}
                {isFolder && item.item_count !== undefined && (
                    <p className="text-xs text-pai-meta mt-1 tnum">
                        {t('fmItemCount', '{n} item{s}', { n: item.item_count, s: item.item_count !== 1 ? 's' : '' })}
                    </p>
                )}
            </div>
        </div>
    );
};

export const FileManagerItemList = ({
    item,
    type,
    isSelected,
    onSelect,
    onOpen,
    onContextMenu,
    onDragStart,
    onDragEnd,
    onDrop,
    isDragging,
    selectedFolders,
    selectedDocuments,
    selectionMode
}) => {
    const { t } = useLanguage();
    const [isDragOver, setIsDragOver] = useState(false);
    const isFolder = type === 'folder';
    const name = isFolder ? item.name : item.original_filename;

    const handleDragStart = (e) => {
        const dragData = {
            type,
            itemId: item.id,
            selectedFolders: isSelected ? Array.from(selectedFolders || []) : [],
            selectedDocuments: isSelected ? Array.from(selectedDocuments || []) : []
        };

        if (!isSelected) {
            if (type === 'folder') {
                dragData.selectedFolders = [item.id];
            } else {
                dragData.selectedDocuments = [item.id];
            }
        }

        e.dataTransfer.setData('application/json', JSON.stringify(dragData));

        const refPayload = {
            type,
            id: item.id,
            name,
            mime_type: item.mime_type || null,
            file_size: item.file_size ?? null,
            selectedFolders: dragData.selectedFolders,
            selectedDocuments: dragData.selectedDocuments,
        };
        e.dataTransfer.setData('application/x-document-ref', JSON.stringify(refPayload));
        e.dataTransfer.effectAllowed = 'copyMove';

        try {
            window.dispatchEvent(new CustomEvent('omnis:document-drag-start', { detail: refPayload }));
        } catch (_) { /* ignore */ }

        if (onDragStart) onDragStart(e, type, item);
    };

    const handleDragOver = (e) => {
        if (!isFolder) return;

        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (!isFolder) return;

        try {
            const dragData = JSON.parse(e.dataTransfer.getData('application/json'));

            if (dragData.type === 'folder' && dragData.selectedFolders?.includes(item.id)) {
                return;
            }

            if (onDrop) {
                onDrop(item.id, dragData.selectedFolders || [], dragData.selectedDocuments || []);
            }
        } catch (err) {
            console.error('Drop error:', err);
        }
    };

    const handleDragEnd = (e) => {
        try {
            window.dispatchEvent(new CustomEvent('omnis:document-drag-end'));
        } catch (_) { /* ignore */ }
        if (onDragEnd) onDragEnd(e);
    };

    // Get status indicator for non-folder items
    const statusIndicator = !isFolder ? getStatusIndicator(item.processing_status, t) : null;

    // Build tooltip text
    const itemCountText = t('fmItemCount', '{n} item{s}', { n: item.item_count || 0, s: item.item_count !== 1 ? 's' : '' });
    const tooltipText = isFolder
        ? `${name}\n${itemCountText}`
        : `${name}\n${formatSize(item.file_size)}\n${formatDate(item.created_at)}${statusIndicator ? `\n${t('fmStatusLine', 'Status: {label}', { label: statusIndicator.label })}` : ''}`;

    const isProtected = isFolder && !!item.is_protected;

    return (
        <tr
            data-item-id={item.id}
            data-item-type={type}
            data-protected={isProtected ? 'true' : undefined}
            draggable
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={(e) => {
                e.stopPropagation();
                // Single click OPENS (folder navigate / document preview) — modern
                // web behaviour (One Click #69). In selection mode, or with a
                // modifier, it selects/toggles instead so bulk-select still works.
                if (selectionMode || e.ctrlKey || e.metaKey || e.shiftKey) onSelect(item.id, type, e);
                else onOpen(item.id, type);
            }}
            onContextMenu={(e) => onContextMenu(e, type, item)}
            title={tooltipText}
            className={`
                cursor-pointer transition-colors border-b border-pai-hairline last:border-b-0
                ${isSelected
                    ? 'bg-white/[0.09]'
                    : 'hover:bg-white/[0.045]'
                }
                ${isDragOver && isFolder
                    ? 'bg-pai-green/[0.10]'
                    : ''
                }
                ${isDragging ? 'opacity-50' : ''}
                ${statusIndicator?.color === 'text-red-500' ? 'bg-pai-red/[0.06]' : ''}
            `}
        >
            {/* Name column */}
            <td className="px-[22px] py-[15px]">
                <div className="flex items-center gap-3">
                    {/* Selection checkbox — shown on every row in selection mode
                        (Select Mission #64); the row click toggles it. Financial
                        Folders (#145): a protected system folder shows a Lock instead
                        — it can't be selected/batch-deleted. */}
                    {selectionMode && (
                        isProtected ? (
                            <span className="flex-shrink-0" data-testid="fm-protected-lock" title={t('fmProtectedNoSelect', "System folder — can't be selected or deleted")}>
                                <Lock size={16} className="text-white/40" />
                            </span>
                        ) : (
                            <span className="flex-shrink-0" data-testid="fm-item-checkbox" aria-checked={isSelected} role="checkbox">
                                {isSelected
                                    ? <CheckSquare size={16} className="text-white" />
                                    : <Square size={16} className="text-white/40" />}
                            </span>
                        )
                    )}
                    {/* A folder keeps its icon (no extension to show); a document gets
                        the design's 30×22 extension chip, still carrying the shared
                        per-type colour + the `fm-item-icon` handle (#180). The small
                        processing-status badge stays as a functional overlay. */}
                    {isFolder ? (
                        <Folder size={20} data-testid="fm-item-icon" data-filetype="folder" className={`${getFileTypeColorClass(name, item.mime_type, true)} flex-shrink-0 ${isDragOver ? 'scale-110 transition-transform' : ''}`} />
                    ) : (
                        <div className="flex-shrink-0 relative">
                            <span
                                data-testid="fm-item-icon"
                                data-filetype={getFileTypeKey(name, item.mime_type, false)}
                                className={`${getFileTypeColorClass(name, item.mime_type, false)} inline-flex h-[22px] w-[30px] items-center justify-center rounded-[5px] border border-white/20 font-mono text-[9px] font-bold uppercase tracking-[0.03em] leading-none`}
                            >
                                {getExtensionLabel(name)}
                            </span>
                            {statusIndicator && (
                                <div className="absolute -top-1 -right-1" title={statusIndicator.label}>
                                    {statusIndicator.icon}
                                </div>
                            )}
                        </div>
                    )}
                    <span
                        data-testid="fm-item-name"
                        className={`text-[14.5px] font-semibold truncate ${statusIndicator?.color === 'text-red-500' ? 'text-pai-red' : 'text-white'}`}
                    >
                        {name}
                    </span>
                    {/* Protected system-folder lock (Financial Folders #145). In
                        selection mode the lock already shows in the checkbox slot. */}
                    {isProtected && !selectionMode && (
                        <span className="flex-shrink-0" data-testid="fm-protected-lock" title={t('fmProtectedNoDelete', "System folder — can't be deleted")}>
                            <Lock size={14} className="text-white/40" />
                        </span>
                    )}
                </div>
            </td>

            {/* Size column */}
            <td className="px-[22px] py-[15px] text-[13.5px] text-white/55 whitespace-nowrap tnum">
                {isFolder ? t('fmItemsCountPlain', '{n} items', { n: item.item_count || 0 }) : formatSize(item.file_size)}
            </td>

            {/* Type column (was "Status" — Status evolution #80): a clean text label. */}
            <td className="px-[22px] py-[15px] text-[13.5px] whitespace-nowrap text-white/55" data-testid="fm-type-cell">
                {getTypeLabel(name, item.mime_type, isFolder, t)}
            </td>

            {/* Versions column (was "Modified" — Date&Version #81): the iteration
                number, shown ONLY for PAI-edited docs (version > 1); empty otherwise. */}
            <td className="px-[22px] py-[15px] text-[13.5px] whitespace-nowrap text-white/55 tnum" data-testid="fm-version-cell">
                {!isFolder && (item.version || 1) > 1 ? `v${item.version}` : ''}
            </td>

            {/* Date column (new — Date&Version #81): the ORIGINAL upload date. */}
            <td className="px-[22px] py-[15px] text-[13.5px] text-pai-meta whitespace-nowrap tnum" data-testid="fm-date-cell">
                {isFolder ? formatDate(item.created_at) : formatDate(item.original_uploaded_at || item.created_at)}
            </td>
        </tr>
    );
};

export { formatSize, formatDate, getFileIcon, getStatusIndicator, getFileTypeColorClass, getFileTypeKey };
